import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import { createItemPriceListNotificationData, deleteItemPriceListNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import useNotification from '../../../core/hooks/use_notification';
import { deleteItemPriceListAction, deleteItemPriceListState } from '../../../features/price/delete_item_price_list';
import { createItemPriceListAction, createItemPriceListState } from '../../../features/price/create_item_price_list';

const PriceListPriceInput = ({ initialPrice, productId, priceListId, refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const [price, setPrice] = useState(initialPrice || '');

    const createItemPriceList = useSelector(createItemPriceListState);
    const deleteItemPriceList = useSelector(deleteItemPriceListState);

    const handleKeyDown = (e) => {
        if (['-', 'e', 'E', '+'].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleBlur = () => {
        if (price === '') return;

        let numValue = parseFloat(price);

        if (numValue < 0.01) {
            setPrice('0.01');
        } else if (numValue > 100) {
            setPrice('100.00');
        } else {
            setPrice(numValue.toFixed(2));
        }
    };

    const handleSave = () => {
        dispatch(createItemPriceListAction({ productId: productId, priceListId: priceListId, basicGrossPrice: price }))
            .then(response => {
                showNotification(createItemPriceListNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            });
    };

    const onRemoveProductPrice = () => {
        dispatch(deleteItemPriceListAction({ productId: productId, priceListId: priceListId }))
            .then(response => {
                showNotification(deleteItemPriceListNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            });
    };

    return (
        <Box sx={ { display: 'flex', flexGrow: 1, alignItems: 'center' } }>
            <TextField
                name="price"
                label="Cena"
                type="number"
                sx={ {
                    width: 100,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                    },
                    '& input[type=number]': {
                        MozAppearance: 'textfield',
                    }
                } }
                fullWidth
                size="small"
                inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }
                InputProps={ {
                    endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                } }
                value={ price }
                onChange={ (e) => setPrice(e.target.value) }
                onKeyDown={ handleKeyDown }
                onBlur={ handleBlur }
            />
            <Tooltip title="Zapisz cenę">
                <IconButton
                    onClick={ handleSave }
                    disabled={ deleteItemPriceList?.loading || createItemPriceList?.loading }
                >
                    {
                        (deleteItemPriceList?.loading || createItemPriceList?.loading)
                            ? <CircularProgress size={ 20 } color="inherit"/>
                            : <SaveIcon/>
                    }
                </IconButton>
            </Tooltip>
            <Tooltip title="Usuń cenę">
                <IconButton
                    onClick={ onRemoveProductPrice }
                    disabled={ deleteItemPriceList?.loading || createItemPriceList?.loading }
                >
                    {
                        (deleteItemPriceList?.loading || createItemPriceList?.loading)
                            ? <CircularProgress size={ 20 } color="inherit"/>
                            : <RemoveCircleIcon/>
                    }
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default PriceListPriceInput;
